/**
 * liveTracks — les pistes que la scène du live montre : la caméra du principal à sa place, celles des
 * petits ronds (les autres tuteurs connectés, une autre caméra du principal), la mienne, l'écran
 * partagé, et celles de mes autres appareils (`twins`). Jamais la caméra d'un autre élève (Wajih,
 * 16-17/10) : elle n'est pas demandée.
 *
 * Lu sur la salle (`useTracks` de `@livekit/components-react`, qui sert aussi le natif) : les pistes
 * souscrites, la mienne comprise. La place de chacune : `pickLiveVideoTracks`.
 */
import { useTracks, type TrackReference } from "@livekit/components-react"
import { RoomEvent, Track } from "livekit-client"
import { useMemo } from "react"

import { type LiveTutorCast } from "../../../../../../engine/live/student/liveTutorCast"
import { type UILiveVideoSource } from "../../../types/studentLive"
import { pickLiveVideoTracks } from "./liveTrackPick"

export type LiveVideoTracks = Partial<Record<UILiveVideoSource, TrackReference>>

/**
 * Ce qui relit les pistes, en plus de ce que LiveKit ajoute toujours (une arrivée, un départ, une piste
 * publiée, souscrite ou retirée) : une piste coupée ou rouverte, que la sélection écarte. Pas la voix :
 * par défaut, chaque `ActiveSpeakersChanged` (plusieurs fois par phrase) relisait les pistes et
 * redessinait tout l'écran du live pour rien.
 */
export const TRACK_EVENTS: RoomEvent[] = [RoomEvent.TrackMuted, RoomEvent.TrackUnmuted]

export function useLiveVideoTracks(cast: LiveTutorCast, twins: readonly string[]): LiveVideoTracks {
    const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], { updateOnlyOn: TRACK_EVENTS })
    return useMemo(() => {
        const picked = pickLiveVideoTracks(
            tracks.map((track) => ({
                track,
                source: track.source,
                identity: track.participant?.identity,
                isLocal: track.participant?.isLocal === true,
                muted: track.participant == null || track.publication == null || track.publication.isMuted,
            })),
            cast,
            twins
        )
        const result: LiveVideoTracks = {}
        for (const [source, entry] of Object.entries(picked)) {
            if (entry != null) result[source as UILiveVideoSource] = entry.track
        }
        return result
    }, [tracks, cast, twins])
}
